import { z } from 'zod';
import { sanitiseSouthAfricanPhoneNumber } from '../phone.js';
import { sanitiseVatNumber } from '../vat.js';
import { sanitiseSarsTaxReference } from '../tax-reference.js';
import { passesVatCheckDigit, passesIncomeTaxCheckDigit, passesPayeCheckDigit } from '../sars-check-digit.js';
import { sanitisedText, nullableSanitisedText } from '../sanitised-text.js';
import { UNIT_TYPES } from '../activity-types.js';
/**
 * Schema for the `unitType` column on a ClientProjectActivity row. Declared
 * early because other schemas (e.g. ResolveRateQuerySchema) reference it
 * before the ClientProjectActivity section. The set of unit types is the
 * source of truth in `activity-types.ts`; this schema is a thin Zod wrapper
 * for HTTP / bulk-import / tool-call validation.
 */
export const UnitTypeSchema = z.enum(UNIT_TYPES);
/**
 * A free-text list-search term (max 200 characters) that PostgreSQL can bind.
 * PostgreSQL text parameters cannot contain NUL (0x00): the driver fails and
 * the route answers 500, so the NUL is refused at the wire schema as a 400.
 * Other control characters stay legal, as a search term is only compared, never
 * rendered.
 *
 * Currently used by the invoice list filters only. Other free-text `search`
 * query parameters still accept a NUL and should adopt this schema.
 */
export const SearchTermSchema = z.string().max(200).refine((value) => !value.includes('\u0000'), { error: 'Search cannot contain a NUL character' });
/**
 * Upper bound for any signed minor-unit (cent) amount that persists to a
 * PostgreSQL `Int` column. Every `amountMinorUnits` column in the schema
 * (ledger entries, bank-statement lines, billing) is int4, whose range ends at
 * 2_147_483_647 (R21,474,836.47). Bounding monetary inputs to this at the wire
 * schema turns a value the database cannot store into a clean 400 at the HTTP
 * boundary, rather than an int4-overflow 500 during Prisma persistence. Use the
 * negative for the lower bound of a signed field. Raising it requires migrating
 * the underlying columns to BigInt first.
 */
export const MAX_MINOR_UNITS = 2_147_483_647;
/**
 * Upper bound on a quantity (`units`) supplied to rate resolution. It is a
 * precision bound rather than a business one: resolving a per-unit rate from a
 * stated total tests divisibility on both figures scaled to integers by 10^6
 * (six decimal places), and a quantity above this would push that product past
 * `Number.MAX_SAFE_INTEGER` once paired with a `MAX_MINOR_UNITS` total, where
 * the test silently stops being exact. Far above any real quantity a firm
 * bills: a million kilometres, pages, or hours on one line.
 */
export const MAX_UNITS = 1_000_000;
/**
 * Upper bound on the BCC addresses one send request may carry, across the
 * invoice and client-statement send flows. Ten is well above any real internal
 * copy list (the send dialogs offer one tick per human account member) while
 * bounding the recipient fan-out a single request can ask SendGrid for.
 *
 * The sending user's own self-copy is NOT counted here: `bccSender` is a
 * separate boolean and the server appends that address to the BCC list after
 * this cap has been applied at the request boundary.
 *
 * Lives here rather than in `invoice.ts` or `client-statement.ts` because both
 * send flows share it - the send dialogs enforce the same ceiling in the UI, so
 * a firm never composes a request the wire schema will reject.
 */
export const MAX_SEND_BCC_RECIPIENTS = 10;
/**
 * Query-string boolean param schema.
 * HTTP query strings carry all values as strings; z.coerce.boolean() treats
 * the string 'false' as truthy. This schema rejects anything other than the
 * literal strings 'true' / 'false' and transforms to a real boolean.
 *
 * Lives here (rather than a domain file) because it is a genuinely
 * cross-domain primitive with no natural single owner - keeping it in the
 * shared base module avoids an import cycle (see "Cross-file references and
 * import cycles" in this package's CLAUDE.md).
 */
export const BooleanQueryParamSchema = z.enum(['true', 'false']).transform((v) => v === 'true');
/**
 * A UUID in any canonical casing, normalised to lower case at the request
 * boundary.
 *
 * Use it for an id the server compares as a string. Our id columns are plain
 * `TEXT` (not Postgres's native `uuid` type) holding Prisma's client-generated,
 * already-lower-cased values, so their equality is case-SENSITIVE, while
 * `z.uuid()` accepts upper case as well. An upper-cased caller id would
 * otherwise miss its own row at any `WHERE id IN (...)`, at an in-memory
 * `Set`/`Map` intersection, and in an idempotency payload hash, where a retry
 * that varied only by case would count as a different request.
 *
 * `.toLowerCase()` rather than a `.transform()`: both keep the input and output
 * types `string`, but a transform is unrepresentable in the OUTPUT JSON schema
 * (it emits `{}`), while `.toLowerCase()` keeps the `uuid` format on both sides.
 * The `satisfies` enforces the `api()` client helper's `ZodType<T, T>`
 * constraint at compile time; test files are excluded from typecheck, so a
 * type-level test could not.
 */
export const LowercaseUuidSchema = z.uuid().toLowerCase();
/**
 * Plain ISO 8601 calendar date (YYYY-MM-DD).
 *
 * The round-trip refine rejects logically invalid calendar dates that the regex
 * allows but JS silently overflows (e.g. 2026-02-29 on a non-leap year,
 * 2026-04-31).
 */
export const CalendarDateOnlySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, {
    error: 'Expected ISO 8601 date (YYYY-MM-DD)',
}).refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || year < 1) {
        return false;
    }
    const d = new Date(Date.UTC(2000, month - 1, day));
    d.setUTCFullYear(year);
    return (d.getUTCFullYear() === year &&
        d.getUTCMonth() === month - 1 &&
        d.getUTCDate() === day);
}, { message: 'Invalid calendar date (e.g. 2026-02-29 is not a valid date)' });
/**
 * Accepts either a plain ISO 8601 calendar date (YYYY-MM-DD) or a full
 * RFC 3339 date-time string. Use this on any field that conceptually
 * represents a calendar day - the domain layer normalises to UTC midnight.
 */
export const CalendarDateSchema = z.union([
    CalendarDateOnlySchema,
    z.iso.datetime(),
]);
/**
 * Lifecycle status of a ClientProject. Lives here (rather than in the client
 * domain file) because it is referenced across domains - the client,
 * bulk-import, and account-provisioning files all consume it. Keeping it in
 * the shared base module avoids a client <-> account import cycle.
 */
export const ClientStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
export const ClientProjectStatusSchema = z.enum(['ACTIVE', 'COMPLETED', 'ARCHIVED', 'INACTIVE']);
export const CLIENT_LIFECYCLE_HEADERS = { 'X-Bill-Client-Lifecycle': '2' };
/**
 * An arbitrary-key JSON object (string keys, unknown values) - the wire shape
 * for free-form metadata bags, tool-call arguments, and similar payloads whose
 * keys are not known ahead of time.
 *
 * Prefer this over `z.record(z.string(), z.unknown())`. They validate
 * identically (any object passes, all keys pass through; non-objects reject),
 * but `z.record` emits a JSON Schema `propertyNames` keyword that
 * fast-json-stringify does not support, so it logs noisy `strictTypes`
 * warnings at response-serialiser compile time. `z.looseObject({})` renders as
 * `{ type: 'object', additionalProperties: {} }` with no `propertyNames`.
 */
export const JsonObjectSchema = z.looseObject({});
// =============================================================================
// Health & Hello
// =============================================================================
export const HelloResponseSchema = z.object({
    message: z.string(),
    timestamp: z.string(),
});
export const HealthResponseSchema = z.object({
    status: z.string(),
    timestamp: z.string(),
    version: z.string(),
});
// =============================================================================
// Error Response
// =============================================================================
export const ErrorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        requestId: z.string(),
        retryable: z.boolean(),
        details: z
            .array(z.object({
            field: z.string(),
            message: z.string(),
        }))
            .optional(),
        context: JsonObjectSchema.optional(),
    }),
});
// =============================================================================
// Pagination
// =============================================================================
export const PaginationQuerySchema = z.object({
    // Cursor is always an entity UUID (see `toPaginatedResult` which returns
    // the last row's `id`). Constrain to UUID so a malformed cursor fails at
    // the validation boundary rather than reaching Prisma and silently
    // producing an incorrectly-paginated result set.
    cursor: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(10),
});
export function createPaginatedResponseSchema(itemSchema) {
    return z.object({
        data: z.array(itemSchema),
        nextCursor: z.string().nullable(),
        hasMore: z.boolean(),
    });
}
// =============================================================================
// Shared Validation
// =============================================================================
/**
 * Zod schema for an SA phone number string. Sanitises formatting characters
 * (brackets, spaces, dashes, dots, etc.) before validating, and parses to
 * the canonical `+27XXXXXXXXX` form. Rejects anything that cannot be coerced
 * into a valid SA number, including a blank string - built on `sanitisedText`
 * (`../sanitised-text.js`).
 */
export const saPhoneNumberSchema = sanitisedText(sanitiseSouthAfricanPhoneNumber, 'Please enter a valid SA phone number, e.g. +27 82 123 4567');
/**
 * Zod schema for an SA VAT number: nullable and optional at source (blank
 * collapses to `null`), sanitising separators (spaces, slashes, hyphens,
 * dots - the same set `sanitiseSarsTaxReference` strips) before validating,
 * and parsing to the canonical 10-digit form. Rejects anything that cannot be
 * coerced into a valid SA VAT number with a static message, so `fromZodError`
 * never reflects user-supplied values. Built on `nullableSanitisedText`
 * (`../sanitised-text.js`) - do not wrap this in a further `.optional()` /
 * `.nullable()` at the call site, it is already both.
 *
 * The inferred VAT check-digit pattern is NOT enforced here: a mismatch is
 * recorded with a warning (`vatCheckDigitWarning`, below) rather than
 * refused, because the arithmetic is a structural inference from the shared
 * SARS reference format rather than a published VAT rule (see
 * `passesVatCheckDigit`'s docstring), and production measurement found
 * existing VAT numbers that do not match it.
 *
 * `SA_VAT_REGEX` and the `sanitiseVatNumber` helper live in `../vat.js`.
 */
export const saVatNumberSchema = nullableSanitisedText(sanitiseVatNumber, 'VAT number must be 10 digits starting with 4', { describe: '10 digits starting with 4; separators are stripped; a check-digit mismatch saves with a warning.' });
// =============================================================================
// File Content Schema
// =============================================================================
/**
 * Zod schema for a base64-encoded file content field.
 *
 * Tagged with `{ fileContent: true }` via Zod `.meta()` so the MCP adapter's
 * build-time assertion can detect and reject any route that exposes this type
 * in its LLM-visible body schema. The agent must supply a `documentId` instead;
 * the adapter resolves it to bytes server-side.
 *
 * Apply to every `data` field that carries raw base64 file content.
 */
export function base64FileContent() {
    return z.string().min(1).meta({ fileContent: true });
}
/** The agent-facing format both SARS reference schemas below describe. */
const SARS_REFERENCE_FORMAT = '10 digits with a valid SARS check digit, leading zeros kept; separators (slashes, spaces, hyphens, dots) are stripped.';
/**
 * Schema for a SARS income tax reference: a nullable, optional 10-digit
 * reference number. Sanitises a non-blank value (see
 * `sanitiseSarsTaxReference`) before requiring it to be exactly 10 digits, so
 * a reference pasted off a document in its slashed form (`1234/567/89/0`) is
 * stored as canonical digits rather than rejected, and additionally requires
 * it to match its SARS check digit (BRS App. B §8.1) - refused, not warned,
 * because every income tax reference in production already passes it.
 * Leading zeros survive the round trip. Built on `nullableSanitisedText`.
 */
export const incomeTaxReferenceSchema = nullableSanitisedText(sanitiseSarsTaxReference, 'Tax reference must be 10 digits', {
    check: { test: passesIncomeTaxCheckDigit, message: 'Tax reference does not match its SARS check digit' },
    // `z.toJSONSchema` drops `.transform()`, so without this the chat agent
    // sees a bare string-or-null and has nothing telling it the format. The
    // describe survives the conversion and is the only place the contract
    // reaches the model.
    describe: `SARS income tax reference: ${SARS_REFERENCE_FORMAT}`,
});
/**
 * Schema for a SARS PAYE employer reference. Same contract as
 * `incomeTaxReferenceSchema`, but checked against the PAYE dispatch (BRS App.
 * B §8.1/§8.2, `passesPayeCheckDigit`) rather than the income tax algorithm -
 * refused, not warned, because every PAYE reference in production already
 * passes it.
 */
export const payeReferenceSchema = nullableSanitisedText(sanitiseSarsTaxReference, 'Tax reference must be 10 digits', {
    check: { test: passesPayeCheckDigit, message: 'Tax reference does not match its SARS check digit' },
    describe: `SARS PAYE employer reference: ${SARS_REFERENCE_FORMAT}`,
});
/**
 * A conduct warning split into a toast heading and a body, so the frontend
 * can render "what happened" and "what to do" separately rather than one
 * long paragraph under a generic heading. Moved from `bank-statement.ts`
 * (the four trust-classify routes' `ClassifyTrustLineResponseSchema`) so a
 * warning primitive shared across domains lives beside the others.
 */
export const WarningDetailSchema = z.object({
    title: z.string(),
    description: z.string(),
});
/** Renders a `WarningDetail` as one line, for a surface that prints plain text (the ledger CLI). */
export function formatWarningDetail(detail) {
    return `${detail.title}. ${detail.description}`;
}
/**
 * The shared `warnings` / `warningDetails` pair a write-route response
 * extends with when its input can trigger a conduct warning (never a
 * refusal - see "Record what happened; guide what should happen" in the root
 * CLAUDE.md). Both optional on the wire: an old server talking to a new
 * bundle, or a new server talking to an old bundle, both parse. The server
 * always emits both (possibly empty) on a route that declares them.
 */
export const writeWarningsFields = {
    warnings: z.array(z.string()).optional(),
    warningDetails: z.array(WarningDetailSchema).optional(),
};
/** The warning shown when a saved VAT number does not match the inferred check-digit pattern. */
export const VAT_CHECK_DIGIT_WARNING_DETAIL = {
    title: 'VAT number saved, but it looks mistyped',
    description: 'It does not match the expected check-digit pattern. Compare it with the VAT registration certificate, or look the vendor up on SARS\'s VAT Vendor Search, and correct it if it differs.',
};
/**
 * The VAT check-digit warning for a given VAT number, or `null` when the
 * number is blank, absent, or passes the check digit. A pure function of the
 * canonical VAT number a write carried - callers derive `warnings` /
 * `warningDetails` from `req.body.vatNumber` at the route layer (see
 * `businessEntityWriteWarnings` in
 * `apps/backend/platform/src/api/routes/helpers/business-entity-response.ts`),
 * never from the stored entity: a write that leaves the VAT number untouched
 * must stay quiet.
 */
export function vatCheckDigitWarning(vatNumber) {
    if (!vatNumber)
        return null;
    return passesVatCheckDigit(vatNumber) ? null : VAT_CHECK_DIGIT_WARNING_DETAIL;
}
/** The warning shown when a VAT registration date is saved on an entity that has no VAT number. */
export const VAT_REGISTERED_FROM_WITHOUT_NUMBER_WARNING_DETAIL = {
    title: 'VAT registration date saved, but it has no effect yet',
    description: 'Bill treats the firm as not VAT-registered until a VAT number is recorded. Add the VAT number, or clear the date.',
};
/**
 * The warning for a saved entity that holds a VAT registration date but no VAT
 * number, or `null` otherwise. Unlike `vatCheckDigitWarning` this reads the
 * persisted pair: a date-only write onto an entity with no number must warn,
 * and so must clearing the number while a date remains. The caller decides
 * whether the write touched either field.
 */
export function vatRegisteredFromWarning(saved) {
    return saved.vatRegisteredFrom && !saved.vatNumber ? VAT_REGISTERED_FROM_WITHOUT_NUMBER_WARNING_DETAIL : null;
}
/**
 * What a classifier said: it offered a ranked answer, or declined to. Lives
 * here rather than in `classification.ts` because `bank-line-proposal.ts`
 * (whose by-lines entry carries a decision outcome) is itself imported by
 * `classification.ts` for `ProposalLegKindSchema` - a cross-domain primitive
 * both sides need, kept in `common.ts` so neither module imports the other
 * (a Zod schema is a runtime value, and a cycle leaves it `undefined` at
 * module load). `classification.ts` re-exports it, so the public name is
 * unchanged.
 */
export const ClassificationOutcomeSchema = z.enum(['SUGGESTED', 'ABSTAINED']);
